/**
 * Day colour derived at render time (4.34) — WEB COPY.
 *
 * Line-equivalent copy of services/kidney/dayPerformance.ts (mobile); only
 * the imports above the marker differ. `__tests__/dayPerformance.test.ts`
 * (root jest) asserts the bodies stay identical. Edit both together.
 */

import type { TrackedNutrientKey } from '@/types'
import type { DayTargets } from '@/kidney/dailyTargets'

// IMPORTS-END (everything below this line is asserted identical across copies)

export type DayPerformance = 'none' | 'green' | 'yellow' | 'red';

export type DayTotals = Partial<Record<TrackedNutrientKey, number>>;

/** Minimal structural view of a Firestore Timestamp (RN Firebase and web JS SDK both fit). */
export interface TimestampLike {
  seconds: number;
  nanoseconds: number;
}

/** `dailySummaries/{date}` fields the classifier reads. */
export interface DayPerformanceDocLike {
  totalItems?: number;
  // Legacy five, written by every client version
  sodium?: number;
  potassium?: number;
  phosphorus?: number;
  protein?: number;
  fluid?: number;
  // 4.34+: every tracked total, stamped in the same write as updatedAt
  totals?: DayTotals | null;
  totalsAt?: TimestampLike | null;
  updatedAt?: TimestampLike | null;
}

/**
 * Fluid is a section of its own; min-only targets are goals, never warnings.
 * 4.39: iron has no default target, so it can only colour a day through a
 * maximum the user entered themselves (like vitamin K).
 */
export const WARN_KEYS: readonly TrackedNutrientKey[] = [
  'sodium', 'potassium', 'phosphorus', 'protein',
  'calories', 'carbs', 'fat', 'magnesium', 'calcium', 'vitaminK', 'iron', 'oxalate',
  'sugars', 'fiber', 'saturatedFat',
];

export function timestampsEqual(a: TimestampLike | null | undefined, b: TimestampLike | null | undefined): boolean {
  if (!a || !b) return false;
  if (typeof a.seconds !== 'number' || typeof b.seconds !== 'number') return false;
  return a.seconds === b.seconds && a.nanoseconds === b.nanoseconds;
}

/**
 * The extended totals are only trustworthy when the last write of the doc
 * stamped them: a client that predates `totals` merges the legacy five plus
 * `updatedAt` and leaves `totals` behind. A pending server timestamp (local
 * write not yet acknowledged) reads as not fresh — the legacy five are then
 * the values that same write just refreshed, so nothing is lost.
 */
export function isTotalsFresh(doc: DayPerformanceDocLike | null | undefined): boolean {
  if (!doc || !doc.totals) return false;
  return timestampsEqual(doc.totalsAt, doc.updatedAt);
}

/** Totals to classify a stored day on: the extended map when fresh, else the legacy five. */
export function totalsFromDoc(doc: DayPerformanceDocLike | null | undefined): DayTotals {
  if (!doc) return {};
  if (isTotalsFresh(doc)) return { ...(doc.totals as DayTotals) };
  const out: DayTotals = {};
  if (typeof doc.sodium === 'number') out.sodium = doc.sodium;
  if (typeof doc.potassium === 'number') out.potassium = doc.potassium;
  if (typeof doc.phosphorus === 'number') out.phosphorus = doc.phosphorus;
  if (typeof doc.protein === 'number') out.protein = doc.protein;
  if (typeof doc.fluid === 'number') out.fluid = doc.fluid;
  return out;
}

/** Nutrients whose ceiling is exceeded — the diary's over-limit list. */
export function overLimitKeys(totals: DayTotals, targets: DayTargets | null): TrackedNutrientKey[] {
  const out: TrackedNutrientKey[] = [];
  if (!targets) return out;
  for (const key of WARN_KEYS) {
    const v = targets.values[key];
    if (!v || v.max === undefined || !(v.max > 0)) continue;   // no ceiling, or min-only
    const total = totals[key] ?? 0;
    if ((total / v.max) * 100 > 100) out.push(key);
  }
  return out;
}

/**
 * none  — nothing logged (the item count is an explicit input: an empty day
 *         and a logged day whose totals are all zero look identical otherwise)
 * green — nothing over · yellow — one over · red — two or more over
 */
export function classifyDay(input: {
  totalItems: number;
  totals: DayTotals;
  targets: DayTargets | null;
}): DayPerformance {
  if (!(input.totalItems > 0)) return 'none';
  const over = overLimitKeys(input.totals, input.targets).length;
  if (over === 0) return 'green';
  if (over === 1) return 'yellow';
  return 'red';
}

/** Convenience for readers holding a stored doc. */
export function classifyStoredDay(doc: DayPerformanceDocLike | null | undefined, targets: DayTargets | null): DayPerformance {
  if (!doc) return 'none';
  return classifyDay({ totalItems: doc.totalItems ?? 0, totals: totalsFromDoc(doc), targets });
}
